import { useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { useSnackbar } from "../../providers/SnackbarProvider";
import {
  ALLOWED_IMAGE_TYPES,
  usePromptInputActions,
} from "../../view_stores/usePromptInputStore";
import {
  SideMenuBehavior,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import ImageDropArea from "../common_components/ImageDropArea";
import SideMenuPanel from "../sideMenu/SideMenuPanel";
import { WelcomeAppBar } from "./components/WelcomeAppBar";
import { WelcomeDescription } from "./components/WelcomeDescription";
import { WelcomeFooter } from "./components/WelcomeFooter";
import WelcomeFreeToTryInfo from "./components/WelcomeFreeToTryInfo";
import { WelcomeBlogFeed } from "./components/WelcomeBlogFeed";
import { WelcomeAnnouncementBanner } from "./components/WelcomeAnnouncementBanner";
import { WelcomeTextField } from "./components/WelcomeTextField";
import { WelcomeSEOText } from "./components/WelcomeSEOText";
import { HeroLightRays } from "./components/HeroLightRays";
import { spacing, layout, colors } from "../../design/tokens";

export function WelcomePageDefault() {
  const isUserSignedIn = useIsUserSignedIn();
  const sideMenuBehavior = useSideMenuBehavior();
  const { setAttachedImage } = usePromptInputActions();
  const [isDragging, setIsDragging] = useState(false);
  const [isValidFileType, setIsValidFileType] = useState<boolean>(true);
  const dragCounter = useRef(0);
  const { showSnackbar } = useSnackbar();

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer?.types.includes("Files")) {
      const items = e.dataTransfer?.items;
      if (items?.length === 1) {
        const item = items[0];
        setIsValidFileType(ALLOWED_IMAGE_TYPES.includes(item.type));
      }
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
      setIsValidFileType(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    setIsValidFileType(true);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setAttachedImage(file);
    } else {
      showSnackbar("Unsupported file type", {
        type: "error",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Darvin - Build Premium Mobile Apps with AI</title>
      </Helmet>
      <div className="h-[100dvh] min-h-[900px] flex">
        {isUserSignedIn && <SideMenuPanel />}
      <div
        className={`${
          isUserSignedIn &&
          (sideMenuBehavior === SideMenuBehavior.PINNED
            ? "pl-[320px]"
            : "pl-[56px]")
        } flex-1 flex flex-col transition-[padding] duration-300
        relative`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="flex-1 flex flex-col relative">
          <WelcomeAnnouncementBanner />
          <div className="min-h-[28%] flex flex-col relative bg-gradient-hero-dark">
            <HeroLightRays />
            {/* <WelcomePromoCode /> */}
            <div className="flex justify-center px-8 relative z-10">
              <div className="w-full max-w-[1300px]">
                <WelcomeAppBar />
              </div>
            </div>
            <div className={`flex-1 flex justify-center items-start px-8 pt-12 pb-40`}>
              <div className={`${layout.container.fullWidth} ${spacing.maxWidth.input} flex flex-col items-center ${spacing.gap.lg}`}>
                <WelcomeDescription />
                <WelcomeTextField />
                <WelcomeFreeToTryInfo />
              </div>
            </div>
            {/* Fade overlay to smoothly transition to blog section */}
            <div className={`${layout.position.absolute} bottom-0 left-0 right-0 h-64 pointer-events-none bg-gradient-to-b from-transparent via-dark-surface-primary/60 to-dark-surface-primary ${layout.zIndex.base}`} />
          </div>
          <div
            className={`flex flex-col items-center justify-start
            ${colors.surface.primary} pt-6`}
          >
            <div
              className={`${layout.container.fullWidth} ${spacing.maxWidth.content} ${layout.container.centered} px-8`}
            >
              {/* <WelcomeTryOutIdeas /> */}
              <WelcomeBlogFeed />
            </div>
          </div>
          <WelcomeSEOText />
          <WelcomeFooter />
          {isDragging && <ImageDropArea isValidFileType={isValidFileType} />}
        </div>
      </div>
    </div>
    </>
  );
}
